import React from 'react';
import {useIsNextJs} from '@practicegenius/next-utils/src/useIsNextJs';
import {
  SocialLoginButtonsProps,
  SocialLoginButtons as SharedSocialLoginButtons,
} from '@practicegenius/pg-ui/src/components/SocialLoginButtons';
import {SocialAuthIntegration} from '@practicegenius/domain/src/data/integrations';
import {localStorageLastUsedSocialProviderManager} from '@practicegenius/pg-ui/src/utils/lastUsedSocialProvider';
import {
  buildSocialProviderCallbackLink,
  buildSocialProviderLoginLink,
} from '@practicegenius/next-utils/src/id-dot/socialAuth';
import {loginWithSocialToken} from '../http/login';
import {PG_CORE_URL} from '../constants/env';
import log from '../utils/logger';
import {getWebsocket} from '../utils/websocket';

type Props = Omit<SocialLoginButtonsProps, 'appProps'> & {
  onLoginError?: (
    error: Error,
    meta: {
      platform: SocialAuthIntegration;
      providerToken: string;
    },
  ) => unknown;
};

export function SocialLoginButtons(props: Props) {
  const isNextJs = useIsNextJs();

  return (
    <SharedSocialLoginButtons
      {...props}
      appProps={{
        getWebsocket,
        lastUsedSocialProviderManager:
          localStorageLastUsedSocialProviderManager,
        logger: log,
        coreServiceUrl: PG_CORE_URL,
        onAuthSuccess: async (platform, payload) => {
          const {providerToken} = payload;
          try {
            if (isNextJs) {
              // In the members-app the social callback page handles authenticating through a code (acquired through the web flow)
              //  and the token (acquired through the mobile flow)
              // @see apps/sites.patientrewardshub.com/src/next/members-app/pages/login/social_provider/callback.tsx
              window.location.href = buildSocialProviderCallbackLink({
                provider: platform,
                token: providerToken,
              });
              return;
            }

            const loginResponse = await loginWithSocialToken(
              platform,
              providerToken,
            );

            window.location.href = loginResponse.redirect;
          } catch (e) {
            props.onLoginError?.(e as Error, {platform, providerToken});
          }
        },
        buildSocialProviderLoginLink,
      }}
    />
  );
}
